import styled from "@emotion/styled";
import { css, keyframes, Theme } from "@emotion/react";
import { CHAT_CONTENT_MAX_WIDTH } from "@features/Chat/chat-helpers";
import { ButtonBase, Typography } from "@mui/material";

export const MessageBarForm = styled.form`
  width: 100%;
  max-width: ${CHAT_CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 0 2.25rem;
  position: relative;
  background-color: ${(p) => p.theme.colors.background};

  &.blocked-overlay-container::before {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    max-height: 100%;
    background-color: rgba(20, 20, 20, 0.7);
    z-index: 100;
    transition: 1000ms;
  }

  ${(p) => p.theme.media.tabletDown} {
    padding: 0.25rem 1rem;
  }
`;
export const MessageBarContainer = styled.div<{ $isMultiline?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-areas: ${(p) =>
    p.$isMultiline
      ? `"text text text" "attach . utility"`
      : `"attach text utility"`};
  gap: 0.75rem;
  padding: ${(p) => (p.$isMultiline ? "0.5rem 1.25rem" : "0.5rem")};
  row-gap: ${(p) => (p.$isMultiline ? "0.5rem" : "0")};
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 0.75rem;
  border: 1px solid ${(p) => p.theme.input.colors.border};
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.input.colors.background};
  transition:
    border-color 0.15s ease,
    row-gap 0.25s ease;

  &:focus-within {
    border-color: ${(p) => p.theme.text.colors.brand};
    outline: 2px solid ${(p) => p.theme.text.colors.brand};
    outline-offset: -1px;
  }

  ${(p) => p.theme.media.mobileDown} {
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
    row-gap: ${(p) => (p.$isMultiline ? "0.5rem" : "0")};
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.7;
    * {
      pointer-events: none;
    }
  }

  &.highlight-container {
    position: relative;
    z-index: 200;

    &::after {
      content: "";
      position: absolute;
      top: 2rem;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 199;
    }
  }

  &:focus {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: -1px;
  }
`;
export const UtilityBar = styled.div`
  grid-area: utility;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const AUTOSIZE_LINE_HEIGHT_EM = 1.2;
export const TextareaWrapper = styled.div<{
  $isListening: boolean;
  $isMultiline: boolean;
}>`
  grid-area: text;
  position: relative;
  width: 100%;
  min-width: 0;
  font-size: ${(p) => p.theme.text.fontSizes.m};
  padding: ${(p) =>
    p.$isMultiline ? "0.5rem 0.375rem 0.5rem 0.625rem" : "0.5rem 0"};
  max-height: ${(p) =>
    p.$isListening
      ? `calc(${AUTOSIZE_LINE_HEIGHT_EM}em + 1rem)`
      : `calc(${AUTOSIZE_LINE_HEIGHT_EM}em * 8 + 1rem)`};
  overflow: hidden;
  transition:
    max-height 0.25s ease,
    padding 0.25s ease;
`;
const textareaTextStyle = (p: { theme: Theme }) => css`
  font-size: ${p.theme.text.fontSizes.m};
  font-family: ${p.theme.text.fonts.primary};
  line-height: ${AUTOSIZE_LINE_HEIGHT_EM};
  white-space: pre-wrap;
  word-wrap: break-word;
`;
export const TextareaMirror = styled.div`
  ${textareaTextStyle};
  visibility: hidden;
`;
export const AutosizeReference = styled.div`
  ${textareaTextStyle};
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  top: -9999px;
  left: -9999px;
`;
export const Textarea = styled.textarea<{
  $isListening?: boolean;
  $isMultiline?: boolean;
}>`
  ${textareaTextStyle};
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: ${(p) => (p.$isMultiline ? "0.5rem 0.25rem" : "0.5rem 0")};
  border: 0;
  color: ${(p) => p.theme.text.colors.primary};
  outline: none;
  background: transparent;
  resize: none;
  overflow-y: auto;
  opacity: ${(p) => (p.$isListening ? 0 : 1)};
  pointer-events: ${(p) => (p.$isListening ? "none" : "auto")};
  transition: opacity 0.15s ease;

  &::placeholder {
    color: ${(p) => p.theme.input.colors.placeholder};
    opacity: 0.7;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;
export const SendButton = styled(ButtonBase)`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 2rem;
  height: 2rem;
  background: ${(p) => p.theme.button.default.colors.background};
  color: ${(p) => p.theme.button.default.colors.text};
  transition: all 0.2s ease-in-out;
  border: solid 1px ${(p) => p.theme.button.default.colors.border};
  border-radius: 50%;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover {
    opacity: 0.7;
  }
`;
export const AttachButton = styled.label`
  grid-area: attach;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  &:active {
    transform: scale(0.9);
  }

  &:hover {
    background-color: ${(p) => p.theme.colors.background5};
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  svg {
    color: ${(p) => p.theme.button.text.colors.text};
  }
`;
export const MicButton = styled.button<{ $isListening?: boolean }>`
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  &:active {
    transform: scale(0.9);
  }

  &:hover {
    background-color: ${(p) => p.theme.colors.background5};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  svg {
    color: ${(p) =>
      p.$isListening
        ? p.theme.text.colors.brand
        : p.theme.button.text.colors.text};
  }
`;
export const DiscardButton = styled(MicButton)`
  grid-area: attach;
`;
const waveBounce = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

export const ListeningWave = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-right: 0.5rem;
`;

export const WaveBar = styled.span<{ $duration: string; $delay: string }>`
  display: inline-block;
  width: 0.2rem;
  height: 1.5rem;
  border-radius: 999px;
  background-color: ${(p) => p.theme.text.colors.brand};
  animation: ${waveBounce} ${(p) => p.$duration} ease-in-out infinite;
  animation-delay: ${(p) => p.$delay};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: scaleY(0.6);
  }
`;
export const FileName = styled(Typography)`
  left: 0;
  font-size: 0.75rem;
  color: ${(p) => p.theme.text.colors.primary};
  margin: 0.5rem 0;
  padding: 0.25rem;
  grid-column: 1 / -1;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  border: solid 1px ${(p) => p.theme.colors.border};
  border-radius: 0.5rem;
  width: fit-content;

  span:first-of-type {
    background: ${(p) => p.theme.colors.background2};
    font-weight: bold;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    text-transform: capitalize;
    font-size: ${(p) => p.theme.text.fontSizes.xs};
  }

  button {
    border: none;
    background: transparent;
    padding: 0;
    width: 20px;
    height: 20px;
    cursor: pointer;
    font-size: ${(p) => p.theme.text.fontSizes.s};
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
export const Disclaimer = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.s};
  text-align: center;
  padding: 0.5rem 3rem;
  background-color: ${(p) => p.theme.colors.background};
  ${(p) => p.theme.media.mobileDown} {
    font-size: ${(p) => p.theme.text.fontSizes.xs};
  }
`;
